import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useDashboardDate } from "@/contexts/DashboardDateContext";
import { useDashboardDataSource } from "@/contexts/DashboardDataSourceContext";
import { DashboardWidget } from "../DashboardWidget";
import { formatCurrency, formatNumber, formatPercent, formatRoas } from "@/lib/formatters";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

type Tab = "spend" | "performance";

interface Props {
  isEditMode: boolean;
  onRemove?: () => void;
  onResize?: () => void;
  onResetSize?: () => void;
}

export function CreativeFormatChart({ isEditMode, onRemove, onResize, onResetSize }: Props) {
  const [tab, setTab] = useState<Tab>("spend");
  const { startDate, endDate } = useDashboardDate();
  const { getUrl, dataKey } = useDashboardDataSource();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["dashboard-breakdown-creative-format", dataKey, startDate, endDate],
    queryFn: async () => {
      const params = new URLSearchParams({ startDate, endDate });
      const res = await fetch(getUrl("breakdown/creative-format", params));
      if (!res.ok) throw new Error("Failed to fetch data");
      return res.json();
    },
  });

  const empty = !isLoading && !error && (!data || data.length === 0);

  return (
    <DashboardWidget title="Creative Format" widgetType="chart" isEditMode={isEditMode} isLoading={isLoading} error={error?.message ?? null} onRetry={() => refetch()} onRemove={onRemove} onResize={onResize} onResetSize={onResetSize}>
      {empty ? (
        <div className="flex items-center justify-center h-full text-sm" style={{ color: "#5F6D82" }} data-testid="empty-creative-format">No creative format data available</div>
      ) : (
        <div className="px-4 pb-3 h-full flex flex-col" data-testid="chart-creative-format">
          <div className="flex gap-1 mb-2">
            {(["spend", "performance"] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                className={`px-2 py-0.5 text-[10px] font-medium rounded-full border transition-colors capitalize ${
                  tab === t ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 text-gray-400 hover:text-gray-600"
                }`}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-0">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data || []} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                <XAxis dataKey="format" tick={{ fontSize: 10, fill: "#5F6D82" }} />
                {tab === "spend" ? (
                  <>
                    <YAxis tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={formatCurrency} width={60} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #DCE3ED" }} formatter={(v: number, name: string) => [name === "spend" ? formatCurrency(v) : formatNumber(v), name === "spend" ? "Spend" : "Purchases"]} />
                    <Bar dataKey="spend" fill="#6366F1" radius={[4, 4, 0, 0]} name="spend" />
                    <Bar dataKey="purchases" fill="#A5B4FC" radius={[4, 4, 0, 0]} name="purchases" />
                  </>
                ) : (
                  <>
                    <YAxis tick={{ fontSize: 10, fill: "#5F6D82" }} tickFormatter={(v: number) => `${v}%`} width={40} />
                    <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid #DCE3ED" }} formatter={(v: number, name: string) => [name === "ctr" ? formatPercent(v) : formatRoas(v), name === "ctr" ? "CTR" : "ROAS"]} />
                    <Bar dataKey="ctr" fill="#10B981" radius={[4, 4, 0, 0]} name="ctr" />
                    <Bar dataKey="roas" fill="#F59E0B" radius={[4, 4, 0, 0]} name="roas" />
                  </>
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </DashboardWidget>
  );
}
